import { store } from '../game/store';
import { drawTitleSky } from '../render/title';
import { LEVELS, levelLabel } from '../levels';
import { type Screen, app, markReady } from './app';
import { openHelp, openSettings } from './dialogs';
import { ICONS, h, icon } from './dom';

/** First campaign level without stars (or the last one when everything is solved). */
export function nextLevelId(): string | null {
  const next = LEVELS.find((l) => !store.progress.levels[l.id]);
  return next ? next.id : null;
}

export function titleScreen(): Screen {
  let lastDraw = 0;
  let drawn = false;
  return {
    title: '',
    mount(root) {
      const nextId = nextLevelId();
      const started = Object.keys(store.progress.levels).length > 0;
      const target = nextId ?? LEVELS[LEVELS.length - 1]!.id;
      const lvl = LEVELS.find((l) => l.id === target)!;
      const play = h(
        'a',
        { class: 'btn primary play-btn', href: `#/play/${target}` },
        h('span', { class: 'play-icon' }, icon(ICONS.play)),
        h(
          'span',
          { class: 'play-text' },
          h('span', {
            class: 'play-big',
            text: !started ? 'Play' : nextId ? 'Continue' : 'Replay',
          }),
          h('span', {
            class: 'play-small',
            text: `Plate ${levelLabel(lvl.id).replace(' · ', '.')} · ${lvl.name}`,
          }),
        ),
      );
      const menuBtn = (href: string, text: string, ic: string) =>
        h('a', { class: 'btn', href }, icon(ic), text);
      const help = h(
        'button',
        { class: 'btn', type: 'button', onclick: () => openHelp('play') },
        icon(ICONS.help),
        'How to play',
      );
      const el = h(
        'div',
        { class: 'title' },
        h(
          'div',
          { class: 'title-corner' },
          h(
            'button',
            {
              class: 'icon-btn',
              type: 'button',
              'aria-label': 'Settings',
              onclick: () => openSettings(),
            },
            icon(ICONS.gear),
          ),
        ),
        h(
          'main',
          { class: 'title-main' },
          h('p', { class: 'label title-kicker', text: 'A puzzle about bending light' }),
          h('h1', { text: 'Lensing' }),
          h('p', {
            class: 'title-tag',
            text: 'Beacons shine, telescopes wait. Your only tool is gravity: place moons, stars and black holes, and every beam that passes them bends.',
          }),
          h(
            'nav',
            { class: 'title-menu', 'aria-label': 'Main menu' },
            play,
            h(
              'div',
              { class: 'title-grid' },
              menuBtn('#/levels', 'Levels', ICONS.grid),
              menuBtn('#/endless', 'Endless', ICONS.dice),
              menuBtn('#/editor', 'Editor', ICONS.edit),
              help,
            ),
          ),
        ),
        h(
          'footer',
          { class: 'title-foot' },
          h(
            'nav',
            { 'aria-label': 'Links' },
            h('a', {
              href: 'https://mustafaobaidd1.github.io/',
              text: '← Mustafa Obaid · Portfolio',
            }),
            h('a', { href: 'https://github.com/mustafaobaidd1/lensing-game', text: 'Source code' }),
            h('a', {
              href: '#/about',
              onclick: (e: Event) => {
                e.preventDefault();
                openHelp('physics');
              },
              text: 'About the physics',
            }),
          ),
          h('span', { text: `${LEVELS.length} plates · endless puzzles · level editor` }),
        ),
      );
      root.append(el);
      markReady();
    },
    unmount() {},
    frame(time) {
      const r = app.renderer;
      const reduced = store.reducedMotion;
      // The background is slow and decorative: about 30 frames per second is plenty.
      if (!reduced && drawn && time - lastDraw < 30) return true;
      lastDraw = time;
      drawn = true;
      r.renderPaper(store.settings.contrast);
      drawTitleSky(r.context, r.width, r.height, r.pixelRatio, time, reduced);
      return !reduced;
    },
    layout() {
      drawn = false;
    },
  };
}
